import { Collapse } from '@helix/collapse'
import { HelixIcon } from '@helix/helix-icon'
import {
  chevron_down as ChevronDownIcon,
  chevron_right as ChevronRightIcon
} from '@helix/helix-icon/outlined'
import { ReactNode, useState } from 'react'
import { Tooltip } from './Tooltip'
import { classNames } from './utils'

export function Collapsible({ children }: { children: ReactNode }) {
  return <div className="-ml-7 flex flex-col">{children}</div>
}

interface CollapsibleItemProps {
  triggerLabel: string
  children: ReactNode
  defaultOpen?: boolean
  collapsible?: boolean
  selected?: boolean
  icon?: (props: any) => ReactNode
  onOpen?: () => void
  onClick?: () => void
}

Collapsible.Item = function CollapsibleCollapse({
  children,
  triggerLabel,
  defaultOpen = false,
  collapsible = true,
  icon,
  onOpen,
  onClick,
  selected = false
}: CollapsibleItemProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen || false)

  return (
    <div className="ml-6">
      <div
        className={classNames(
          'flex h-[36px] w-full items-center gap-1.5 rounded-[6px] p-[8px]',
          {
            'bg-brand-primary': selected,
            'focus-within:bg-brand-white hover:bg-brand-white': !selected
          }
        )}>
        {collapsible && (
          <button
            type="button"
            aria-controls="expandable-div"
            onClick={(e) => {
              e.stopPropagation()
              if (collapsible) {
                setIsOpen(!isOpen)
              }
            }}
            className={classNames(
              'shrink-0 cursor-pointer border-0 bg-transparent p-0',
              {
                'cursor-default': selected
              }
            )}>
            <HelixIcon
              className={classNames('shrink-0', {
                'helix-svg-fill--white active:helix-svg-fill--gray-600':
                  selected,
                'helix-svg-fill--gray-600': !selected,
                invisible: !collapsible
              })}
              icon={isOpen ? ChevronDownIcon : ChevronRightIcon}
            />
          </button>
        )}
        <button
          type="button"
          tabIndex={0}
          className={classNames(
            'flex flex-1 items-center gap-1.5 border-0 bg-transparent p-0',
            {
              'cursor-default': selected
            }
          )}
          onClick={() => onClick?.()}
          aria-label={triggerLabel}>
          {icon && (
            <HelixIcon
              icon={icon}
              className={classNames('shrink-0', {
                'helix-svg-fill--white active:helix-svg-fill--gray-600':
                  selected,
                'helix-text-gray-600': !selected
              })}
            />
          )}
          {triggerLabel.length > 19 ? (
            <Tooltip content={triggerLabel}>
              <span
                className={classNames('text-nowrap helix-body truncate', {
                  'text-white': selected,
                  'text-gray-900': !selected
                })}>
                {triggerLabel.slice(0, 19) + '...'}
              </span>
            </Tooltip>
          ) : (
            <span
              className={classNames('text-nowrap helix-body truncate', {
                'text-white': selected,
                'text-gray-900': !selected
              })}>
              {triggerLabel}
            </span>
          )}
        </button>
      </div>
      <Collapse id="expandable-div" isOpen={isOpen} onOpening={onOpen}>
        {children}
      </Collapse>
    </div>
  )
}
